import ProductsActionCreators from "@/Actions/Products";
import { processCustomFieldFormValues } from "@/Utils";
import useDispatch from "@/Components/Utils/useDispatch";
import useCommonFunctions from "@/Hooks/Products/useProductDrawerCommonFunctions";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import { GlobalState } from "@/Reducers/interface";
import AddTaxCategoryDrawer from "@pages/TaxListPage/Drawers/TaxCategory";
import ZModal from "@atoms/ZModal";
import {
	BundleProduct,
	EditOrCloneProductDrawerMode,
	ProductFormValues,
	ProductPricingPlan,
	ProductType
} from "@/Contracts/Products";
import { PricingPlanNames, UserRole } from "@/Contracts/Users";
import ZDrawer from "@molecules/ZDrawer";
import ZForm from "@organisms/ZForm";
import { IFormProps } from "@organisms/ZForm/interface";
import Form from "antd/lib/form";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { EProductDrawerState } from "../../Product/interface";
import { IEditBundleChildToProductDrawerProps } from "./interface";

const EditBundleToChildProductDrawer: React.FC<
	IEditBundleChildToProductDrawerProps
> = props => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const isAdminOrManager = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.SecondaryRole
	);

	const { isLoading, addTaxCategoryDrawerOpen } = useSelector(
		(state: GlobalState) => ({
			isLoading: state.products.isLoading,
			addTaxCategoryDrawerOpen: state.user.addTaxCategoryDrawerOpen
		})
	);

	useEffect(() => {
		form.setFieldsValue({ supplier_id: props.supplierId });
	}, [props.supplierId]);

	const resetState = () => {
		resetPartialState();
	};

	const onClose = () => {
		resetState();
		props.onClose && props.onClose();
	};

	const {
		sections,
		currentProduct,
		closeAddTaxCategoryDrawer,
		handleTaxCategoryDrawerOnSubmit,
		setCurrentProductType,
		customFieldList,
		resetPartialState,
		setProductImageList,
		setCurrentProduct,
		resetDynamicForm
	} = useCommonFunctions(
		form,
		EProductDrawerState.BUNDLECHILDEDIT,
		null,
		[],
		null,
		null,
		EditOrCloneProductDrawerMode.EDIT,
		props.isReadonly,
		props.pricing
	);

	const onFinish = (editProductFormValues: ProductFormValues) => {
		const isProductExistInBundle = props.bundledProductList.find(
			bundleProduct =>
				bundleProduct.item_id === props.currentActiveProduct?.item_id
		);

		if (isProductExistInBundle) {
			ZModal.confirm({
				title: "Please Confirm",
				content:
					"Product already added to the bundle. Update the product with new values?",
				okText: "Yes",
				onOk: () => {
					editProduct(editProductFormValues);
				}
			});
		} else {
			editProduct(editProductFormValues);
		}
	};

	const editProduct = async (editProductFormValues: ProductFormValues) => {
		dispatch(ProductsActionCreators.showProductsLoader());
		try {
			if (!currentProduct) {
				throw new Error("No default value on the drawer");
			}
			let editChildProductRequest: BundleProduct = {
				...currentProduct,
				...editProductFormValues,
				mandatory_custom_field_check_require: true
			};

			const customFieldFormValues =
				editProductFormValues.custom_field_form_values;
			if (customFieldFormValues && customFieldList?.length > 0) {
				editChildProductRequest = {
					...editChildProductRequest,
					item: {
						...editChildProductRequest.item,
						custom_fields: processCustomFieldFormValues(
							customFieldFormValues,
							customFieldList
						),
						mandatory_custom_field_check_require: true
					}
				};
			}

			if (!editChildProductRequest.selected_pricing_id) {
				const pricingList = (
					editChildProductRequest as Record<string, unknown>
				).pricing as ProductPricingPlan[] | undefined;
				const selfPricing = pricingList?.find(
					(p: ProductPricingPlan) =>
						!p.amazon_business_pricing_details &&
						!p.distributor_pricing_details
				);
				editChildProductRequest.selected_pricing_id =
					selfPricing?.id ?? null;
			}
			delete (editChildProductRequest as Record<string, unknown>).pricing;

			await props.onSubmit(editChildProductRequest);
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(ProductsActionCreators.hideProductsLoader());
		}
	};

	useEffect(() => {
		if (props.visible) {
			if (props.currentActiveProduct) {
				const curProduct = {
					...props.currentActiveProduct.item,
					...props.currentActiveProduct
				};
				const curProductType = curProduct.type ?? ProductType.Software;
				const curProductImageList = curProduct?.item?.item_images ?? [];
				setProductImageList([...curProductImageList]);
				setCurrentProduct(curProduct);
				setCurrentProductType(curProductType);
			}
		} else {
			resetState();
		}
	}, [props.visible, props.currentActiveProduct]);

	useEffect(() => resetDynamicForm(), [currentProduct]);

	const formProps: IFormProps = {
		onFinish,
		key: "BundleToChildProductForm",
		sections,
		loading: isLoading,
		submitButtonText: "Save",
		isStickFooter: true,
		form,
		isCancelAllowed: true,
		onCancel: onClose,
		isRightFooter: true,
		isDynamic: true,
		shouldDisableBeforeTouch: false,
		disabled: currentProduct?.read_only || false
	};

	return (
		<ZDrawer
			visible={props.visible}
			closable={true}
			destroyOnClose={true}
			onClose={onClose}
			title={`${props.isFromAddDrawer ? "Add" : "Edit"} Product`}
			removeDefaultPadding={true}
		>
			{isAdminOrManager && (
				<>
					{currentProduct && (
						<ZForm {...formProps} className="py-4" />
					)}
					<AddTaxCategoryDrawer
						visible={addTaxCategoryDrawerOpen}
						onClose={closeAddTaxCategoryDrawer}
						handleTaxCategoryDrawerOnSubmit={
							handleTaxCategoryDrawerOnSubmit
						}
					/>
				</>
			)}
		</ZDrawer>
	);
};

export default EditBundleToChildProductDrawer;
